import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, Shield, Zap, ArrowRight, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RevealText, Reveal } from "@/lib/motion";
import { api, divisions, errMsg} from "@/lib/config";

const AREA_KEYS = [
  { key: "irrigazione", name: "Irrigazione" },
  { key: "trattamento-acqua", name: "Trattamento Acque" },
  { key: "automazioni", name: "Automazioni" },
  { key: "elettrico", name: "Impianti Elettrici" },
  { key: "fotovoltaico", name: "Fotovoltaico" },
  { key: "antiincendio", name: "Antincendio" },
];

const PLAN_ACCENT = {
  tipo1: { border: "border-white/20", chip: "text-white/70", accent: "text-white", tag: "Standard" },
  tipo2: { border: "border-[#ff4b00]/50", chip: "text-[#ff4b00]", accent: "text-[#ff4b00]", tag: "Consigliato" },
  tipo3: { border: "border-white/80", chip: "text-white", accent: "text-white", tag: "Premium" },
};

export default function AssistenzaPage() {
  const [data, setData] = useState(null);
  const [area, setArea] = useState("irrigazione");
  const [plants, setPlants] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "" });
  const [chosenPlan, setChosenPlan] = useState("tipo2");
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/service/plans").then((r) => setData(r.data)).catch(() => toast.error("Impossibile caricare i piani"));
  }, []);

  const mult = data?.divisions?.find((d) => d.key === area)?.multiplier || 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Nome ed email obbligatori");
    if (!privacy) return toast.error("Accetta la privacy policy per procedere");
    setSubmitting(true);
    try {
      await api.post("/service/contract-requests", {
        ...form, plan_code: chosenPlan, area_key: area, plants_count: plants,
      });
      try { await api.post("/privacy/consent-log", { kind: "form", form: "contract_request", email: form.email }); } catch {}
      toast.success("Richiesta preventivo inviata! Ti contatteremo entro 24h lavorative con un'offerta su misura.");
      setForm({ name: "", email: "", phone: "", company: "", notes: "" });
      setPrivacy(false);
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pt-24 pb-24">
      {/* Hero */}
      <section className="relative py-20 md:py-28 border-b border-[#141414] blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#ff4b00]" />
              <span className="font-mono-tech text-white/70">Assistenza · SLA · Manutenzione</span>
            </div>
          </Reveal>
          <RevealText text="Contratti di assistenza." className="font-display text-white text-[10vw] md:text-[6vw] leading-[0.9]" />
          <Reveal delay={0.2}>
            <p className="text-white/70 max-w-2xl mt-6 md:text-lg">
              Tre livelli di copertura, sei divisioni. Scegli l'SLA che serve al tuo impianto:
              standard, esteso o H24/7. Manutenzione programmata inclusa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Config: area + plants */}
      <section className="py-12 border-b border-[#141414]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">Divisione impianto</div>
            <div className="flex flex-wrap gap-2">
              {AREA_KEYS.map((a) => (
                <button key={a.key} onClick={() => setArea(a.key)}
                  data-testid={`area-tab-${a.key}`}
                  className={`font-mono-tech text-[11px] px-4 py-2.5 border ${area === a.key ? "border-[#ff4b00] text-[#ff4b00]" : "border-[#222] text-white/60 hover:border-white/40"}`}>
                  {a.name}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="font-mono-tech text-[10px] text-white/40 mb-3">Numero di impianti</div>
            <div className="flex items-center border border-[#222] w-fit">
              <button onClick={() => setPlants(Math.max(1, plants - 1))} className="w-11 h-11 hover:bg-[#111]">−</button>
              <div className="w-16 text-center font-mono-tech">{plants}</div>
              <button onClick={() => setPlants(Math.min(50, plants + 1))} className="w-11 h-11 hover:bg-[#111]">+</button>
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {data ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.plans.map((p) => {
                const acc = PLAN_ACCENT[p.code];
                const selected = chosenPlan === p.code;
                return (
                  <motion.button
                    key={p.code}
                    onClick={() => setChosenPlan(p.code)}
                    data-testid={`plan-${p.code}`}
                    whileHover={{ y: -4 }}
                    className={`text-left p-8 border ${selected ? "border-[#ff4b00]" : acc.border} bg-[#0a0a0a] relative transition-colors`}
                  >
                    {p.code === "tipo2" && (
                      <div className="absolute top-0 right-0 bg-[#ff4b00] text-black font-mono-tech text-[10px] px-3 py-1">CONSIGLIATO</div>
                    )}
                    <div className={`font-mono-tech ${acc.chip}`}>◐ Tipo {p.priority} · {acc.tag}</div>
                    <div className={`font-display text-5xl md:text-6xl mt-4 ${acc.accent}`}>{p.name}</div>
                    <div className="font-mono-tech text-white/50 text-[11px] mt-3">{p.coverage_desc}</div>
                    <div className="mt-8 inline-flex items-center gap-2 border border-[#1a1a1a] px-3 py-2 bg-[#080808]">
                      <span className="w-1.5 h-1.5 bg-[#ff4b00] rounded-full" />
                      <span className="font-mono-tech text-[10px] text-white/70">Prezzo su preventivo</span>
                    </div>
                    <div className="font-mono-tech text-[10px] text-white/40 mt-3">
                      SLA intervento: entro {p.sla_hours}h
                    </div>
                    <ul className="mt-8 space-y-3">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-white/80 text-sm">
                          <Check size={14} className={`${acc.accent} mt-0.5 shrink-0`} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 flex items-center justify-between border-t border-[#1a1a1a] pt-4">
                      <span className="font-mono-tech text-[10px] text-white/40">{selected ? "SELEZIONATO" : "SELEZIONA"}</span>
                      <span className={`w-4 h-4 border ${selected ? "bg-[#ff4b00] border-[#ff4b00]" : "border-white/40"}`} />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          ) : <div className="text-center font-mono-tech text-white/40">Caricamento piani...</div>}
        </div>
      </section>

      {/* Per-hour: descriptive without prices */}
      {data?.hourly_rates && (
        <section className="py-16 border-y border-[#141414] bg-[#080808]">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              <div className="md:col-span-4">
                <div className="font-mono-tech text-[10px] text-white/40 mb-3">Non hai un contratto?</div>
                <div className="font-display text-4xl md:text-5xl leading-[0.95]">Interventi<br/>a tempo.</div>
                <p className="text-white/60 mt-4 text-sm">Perfetti per manutenzioni straordinarie una tantum, guasti isolati o assistenza fuori garanzia. Ogni intervento viene quotato su preventivo.</p>
              </div>
              <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {data.hourly_rates.map((h) => {
                  // Strip pricing bits from notes (anything containing €)
                  const cleanNote = (h.notes || "")
                    .split(/[·,]/)
                    .map((s) => s.trim())
                    .filter((s) => s && !s.includes("€") && !/\bkm\b/i.test(s))
                    .join(" · ");
                  return (
                    <div key={h.code} className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
                      <div className="font-mono-tech text-[10px] text-white/40">{h.code === "emergency" ? "Emergenza" : h.code === "extended" ? "Extra" : "Ordinario"}</div>
                      <div className="font-display text-2xl mt-3 text-white">{h.name}</div>
                      {cleanNote && <div className="font-mono-tech text-[10px] text-white/40 mt-4">{cleanNote}</div>}
                      <div className="mt-5 pt-4 border-t border-[#1a1a1a] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-[#ff4b00] rounded-full" />
                        <span className="font-mono-tech text-[10px] text-white/60">Su preventivo</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Request form */}
      <section id="richiesta" className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="num-chip mb-4"><span className="dot" /> Richiedi il tuo preventivo</div>
            <RevealText text="Un tecnico ti richiama." className="font-display text-white text-[9vw] md:text-[3.6vw] leading-[0.95]" />
            <div className="mt-8 space-y-4 text-white/70 text-sm">
              <div className="flex items-start gap-3"><Shield size={16} className="text-[#ff4b00] mt-0.5" /> <span>Preventivo personalizzato entro 24h lavorative</span></div>
              <div className="flex items-start gap-3"><Clock size={16} className="text-[#ff4b00] mt-0.5" /> <span>Contratto attivabile in 3-5 gg dall'accettazione</span></div>
              <div className="flex items-start gap-3"><Zap size={16} className="text-[#ff4b00] mt-0.5" /> <span>Cancellabile con 90 giorni di preavviso</span></div>
            </div>
            <div className="mt-8 border border-[#1a1a1a] bg-[#0a0a0a] p-5">
              <div className="font-mono-tech text-white/40 text-[10px] mb-3">Riepilogo richiesta</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-[10px] text-white/40">Piano</span>
                  <span className="font-display text-lg text-[#ff4b00]">{chosenPlan.toUpperCase()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-[10px] text-white/40">Divisione</span>
                  <span className="font-mono-tech text-[11px] text-white">{AREA_KEYS.find((a) => a.key === area)?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-[10px] text-white/40">Impianti</span>
                  <span className="font-mono-tech text-[11px] text-white">{plants}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#ff4b00] rounded-full" />
                <span className="font-mono-tech text-[10px] text-white/70">Prezzo su preventivo personalizzato</span>
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="md:col-span-7 md:pl-10 md:border-l md:border-[#1a1a1a] space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div><label className="field-label">Nome e cognome *</label><input required data-testid="assist-name" className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="field-label">Email *</label><input required type="email" data-testid="assist-email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="field-label">Telefono</label><input data-testid="assist-phone" className="field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="field-label">Azienda</label><input data-testid="assist-company" className="field" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="field-label">Descrivi il tuo impianto (indirizzo, potenza, età, marca...)</label><textarea data-testid="assist-notes" rows={4} className="field resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <label className="flex items-start gap-3 text-white/70 text-sm cursor-pointer pt-2">
              <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="assist-privacy" className="mt-1 accent-[#ff4b00]" />
              <span>Ho letto e accetto la <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link>. Autorizzo il trattamento dei miei dati per rispondere alla richiesta. *</span>
            </label>
            <div className="flex items-center justify-between pt-4 gap-4">
              <div className="font-mono-tech text-[10px] text-white/40 max-w-xs">Ti richiamiamo entro 24h lavorative.</div>
              <button disabled={submitting} data-testid="assist-submit" className="btn-primary">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Richiedi preventivo <Send size={14} /></>}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Ticket CTA */}
      <section className="py-20 border-t border-[#141414] bg-[#080808]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="num-chip mb-4"><span className="dot" /> Hai già un impianto?</div>
            <div className="font-display text-4xl md:text-5xl leading-[0.95]">Apri un ticket.</div>
            <p className="text-white/60 mt-4 max-w-md text-sm">
              Segnalaci il problema: un tecnico Gefion ti risponderà in base al tuo piano SLA.
              Se hai già un'area riservata, entra dal login per collegare il ticket al tuo contratto.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/ticket" data-testid="cta-open-ticket" className="btn-primary"><ArrowRight size={14} /> Apri ticket</Link>
            <Link to="/area/login" data-testid="cta-customer-login" className="btn-ghost">Area riservata</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
